/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { Button, Card, Col, Form, message, Row, Space } from 'antd';
import { Formik } from 'formik';
import { omit } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type AutoplayModuleSpecsFragment } from '../../../../api/fragments/AutoplayModuleSpecs';
import { type AutoplaySettingSpecsFragment } from '../../../../api/fragments/AutoplaySettingSpecs';
import {
    type UpdateAutoplayModuleSettingMutation,
    type UpdateAutoplayModuleSettingMutationVariables,
    UpdateAutoplayModuleSettingDocument,
} from '../../../../api/mutations/updateAutoplayModuleSetting';
import { type AutoplaySettingPayload } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import InputNumberField from '../../../../components/fields/InputNumberField';
import PasswordField from '../../../../components/fields/PasswordField';
import SelectField from '../../../../components/fields/SelectField';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../../utilities/useHandleError';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import useValidator from '../../../../utilities/useValidator';
import validator from './validators';

export type FormValues = AutoplaySettingPayload;

export type UpdateAutoplaySettingInnerPageProps = {
    module: AutoplayModuleSpecsFragment;
    autoplaySettings: AutoplaySettingSpecsFragment;
};

const colSpan = { lg: 8, md: 12, xs: 24 };

const UpdateAutoplaySettingInnerPage = ({ module, autoplaySettings }: UpdateAutoplaySettingInnerPageProps) => {
    const moduleId = module.id;
    const autoplaySettingId = autoplaySettings.id;

    const { t } = useTranslation(['autoplayModuleDetails']);
    const apolloClient = useApolloClient();
    const navigate = useNavigate();

    const { autoplayVersionOptions } = useSystemOptions();

    const initialValues = useMemo(
        (): FormValues => ({
            ...omit(['id', '__typename', 'companyId', 'moduleId'], autoplaySettings),
        }),
        [autoplaySettings]
    );

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('autoplayModuleDetails:autoplaySettingsDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateAutoplayModuleSettingMutation, UpdateAutoplayModuleSettingMutationVariables>({
                    mutation: UpdateAutoplayModuleSettingDocument,
                    variables: { moduleId, settingId: autoplaySettingId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('autoplayModuleDetails:autoplaySettingsDrawer.messages.updateSuccess'),
            });

            navigate(`/admin/system/modules/${moduleId}`);
        },
        [t, apolloClient, moduleId, autoplaySettingId, navigate]
    );

    const validate = useValidator(validator());

    return (
        <ConsolePageWithHeader
            footer={[
                <Button key="submit" form="autoplaySettingsForm" htmlType="submit" type="primary">
                    {t('autoplayModuleDetails:autoplaySettingsDrawer.updateButton')}
                </Button>,
            ]}
            onBack={() => navigate(`/admin/system/modules/${moduleId}`)}
            title={t('autoplayModuleDetails:autoplaySettingsDrawer.title.update')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form
                        id="autoplaySettingsForm"
                        layout="vertical"
                        name="autoplaySettingsForm"
                        onSubmitCapture={handleSubmit}
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Card title={t('autoplayModuleDetails:dividers.mainSettings')}>
                                <Row gutter={14}>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t('autoplayModuleDetails:autoplaySettingsDrawer.fields.displayName', {
                                                returnObjects: true,
                                            })}
                                            name="displayName"
                                            required
                                        />
                                    </Col>
                                </Row>
                            </Card>
                            <Card title={t('autoplayModuleDetails:dividers.autoplaySettings')}>
                                <Row gutter={14}>
                                    <Col {...colSpan}>
                                        <PasswordField
                                            {...t('autoplayModuleDetails:autoplaySettingsDrawer.fields.apiKey', {
                                                returnObjects: true,
                                            })}
                                            name="apiKey"
                                            required
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <PasswordField
                                            {...t('autoplayModuleDetails:autoplaySettingsDrawer.fields.apiToken', {
                                                returnObjects: true,
                                            })}
                                            name="apiToken"
                                            required
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t('autoplayModuleDetails:autoplaySettingsDrawer.fields.apiEndpoint', {
                                                returnObjects: true,
                                            })}
                                            name="apiEndpoint"
                                            required
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <SelectField
                                            {...t('autoplayModuleDetails:autoplaySettingsDrawer.fields.apiVersion', {
                                                returnObjects: true,
                                            })}
                                            name="apiVersion"
                                            options={autoplayVersionOptions}
                                            required
                                            showSearch
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputNumberField
                                            {...t(
                                                'autoplayModuleDetails:autoplaySettingsDrawer.fields.concurrentTasks',
                                                {
                                                    returnObjects: true,
                                                }
                                            )}
                                            name="concurrentTasks"
                                            required
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputNumberField
                                            {...t(
                                                'autoplayModuleDetails:autoplaySettingsDrawer.fields.concurrentSeconds',
                                                {
                                                    returnObjects: true,
                                                }
                                            )}
                                            name="concurrentSeconds"
                                            required
                                        />
                                    </Col>
                                </Row>
                            </Card>
                        </Space>
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default UpdateAutoplaySettingInnerPage;
